/**
 * ProfileCommunitiesPanel — own-profile right-rail card listing communities.
 *
 * Uses useCommunities() to show a curated/trending subset (up to 3 rows),
 * preferring joined communities, then falling back to the full list.
 * Each row shows name + member count — presentational, no join affordance needed
 * on the owner's own profile rail.
 */
import styled from 'styled-components';
import { Panel, SectionLabel } from '@/ui';
import { useCommunities } from '@/hooks';
import type { Community } from '@/core/models';

/* ── Styled helpers ─────────────────────────────────────────────────────────── */

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.brandFrom};
  overflow: hidden;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CommunityName = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Members = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const EmptyState = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Row component ──────────────────────────────────────────────────────────── */

function CommunityListRow({ community }: { community: Community }) {
  // Show the first letter of the community name as avatar initial
  const initial = community.name.charAt(0).toUpperCase();

  return (
    <Row>
      <Avatar aria-hidden="true">{initial}</Avatar>
      <Info>
        <CommunityName>{community.name}</CommunityName>
        {/* community.members is already a display string incl. the unit, e.g. "142K members". */}
        <Members>{community.members}</Members>
      </Info>
    </Row>
  );
}

/* ── Panel ──────────────────────────────────────────────────────────────────── */

const MAX_DISPLAY = 3;

export function ProfileCommunitiesPanel() {
  const { communities, joined } = useCommunities();

  // Prefer communities the user has joined; fall back to all (curated/trending subset)
  const source = joined.length > 0 ? joined : communities;
  const display = source.slice(0, MAX_DISPLAY);

  return (
    <Panel>
      <SectionLabel as="h2">Communities</SectionLabel>

      {display.length === 0 ? (
        <EmptyState>No communities yet.</EmptyState>
      ) : (
        <List>
          {display.map((community) => (
            <CommunityListRow key={community.id} community={community} />
          ))}
        </List>
      )}
    </Panel>
  );
}
